import { useEffect, useRef } from "react";
import { useAuth } from "@/contexts/AuthContext";
import api from "@/lib/api";
import { toast } from "sonner";
import { Flame, Sun } from "lucide-react";

const KIND_META = {
  streak_risk: { icon: Flame, tone: "border-amber-200 bg-amber-50", iconBg: "bg-amber-100 text-amber-700 border-amber-200", label: "Streak at risk" },
  daily_start: { icon: Sun, tone: "border-primary-200 bg-primary-50/60", iconBg: "bg-primary-50 text-primary-700 border-primary-200", label: "Daily focus" },
};

/**
 * Polls /api/performance/notifications every 60s and surfaces newly arrived
 * streak/daily notifications as toasts. Gated to admin while the
 * Performance feature is in pre-rollout testing.
 *
 * On first poll we index existing IDs without toasting (no spam on login).
 * Mount once below <AuthProvider>.
 */
export default function PerformanceNotifProvider() {
  const { user } = useAuth();
  const seen = useRef(new Set());
  const initialized = useRef(false);

  useEffect(() => {
    if (!user || user.role !== "admin") return undefined;
    let cancelled = false;
    let timer = null;

    const tick = async () => {
      try {
        const { data } = await api.get("/performance/notifications", { params: { limit: 20, unread_only: true } });
        const rows = data?.rows || [];
        if (!initialized.current) {
          rows.forEach((n) => seen.current.add(n.id));
          initialized.current = true;
        } else {
          rows.slice().reverse().forEach((n) => {
            if (seen.current.has(n.id)) return;
            seen.current.add(n.id);
            const meta = KIND_META[n.kind] || KIND_META.daily_start;
            const Icon = meta.icon;
            toast.custom(
              (tid) => (
                <div
                  className={`flex items-start gap-3 p-4 rounded-2xl border shadow-soft max-w-sm bg-card ${meta.tone}`}
                  data-testid={`perf-notif-toast-${n.id}`}
                  onClick={() => toast.dismiss(tid)}
                >
                  <div className={`h-10 w-10 grid place-items-center rounded-2xl shrink-0 border ${meta.iconBg}`}>
                    <Icon className="h-4 w-4" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-[10px] uppercase tracking-[0.18em] font-bold text-muted-foreground">{meta.label}</p>
                    <p className="text-sm font-semibold mt-0.5 truncate">{n.title}</p>
                    <p className="text-xs text-muted-foreground mt-0.5 leading-snug">{n.message}</p>
                  </div>
                </div>
              ),
              { duration: 9_000 },
            );
          });
        }
      } catch {
        /* silent — non-critical */
      }
      if (!cancelled) timer = setTimeout(tick, 60_000);
    };

    timer = setTimeout(tick, 1_500);
    return () => { cancelled = true; if (timer) clearTimeout(timer); };
  }, [user]);

  return null;
}
